/**
 * KT1 capture strategies (FR-71, NFR-01).
 *
 *   worklet-native  AudioWorklet in an AudioContext at the device rate
 *   worklet-16k     AudioWorklet in an AudioContext requested at 16 kHz
 *   media-recorder  MediaRecorder with the browser's default container
 *
 * Every session records a timeline of lifecycle events (context state, track
 * mute/ended, page visibility) so interruptions on iOS can be diagnosed.
 * Nothing leaves the device.
 */

import { analyseSignal, concatChunks, type SignalStats } from './analysis.ts'
import { describeError } from './capabilities.ts'
import {
  CAPTURE_PROCESSOR_NAME,
  CAPTURE_PROCESSOR_SOURCE,
  type ProcessorMessage,
} from './capture-processor.ts'
import { encodeWavPcm16 } from './wav.ts'

export type CaptureStrategy = 'worklet-native' | 'worklet-16k' | 'media-recorder'
export type StopReason = 'user' | 'max-duration' | 'track-ended' | 'error'

export const MAX_CAPTURE_SECONDS = 30
const FLUSH_TIMEOUT_MS = 1000
/** Setup budget after the microphone is granted (context resume, worklet load). */
const START_TIMEOUT_MS = 8000

export interface TimelineEvent {
  /** Milliseconds since the session was created. */
  t: number
  event: string
}

/** Serializable capture description. Never contains audio. */
export interface CaptureReport {
  strategy: CaptureStrategy
  voiceProcessing: boolean
  startedAt: string
  stopReason: StopReason
  error: string | null
  wallClockSeconds: number | null
  trackSettings: Record<string, unknown> | null
  requestedSampleRate: number | null
  contextSampleRate: number | null
  contextStateAtStart: string | null
  output: {
    mimeType: string
    sampleRate: number | null
    bytes: number
  } | null
  capturedFrames: number | null
  expectedFrames: number | null
  /** captured / expected frames; well below 1 indicates dropouts. */
  captureRatio: number | null
  signal: SignalStats | null
  /** For MediaRecorder output: decoded via Web Audio, which may resample. */
  decodeNote: string | null
  timeline: TimelineEvent[]
}

export interface CaptureOutcome {
  report: CaptureReport
  audio: Blob | null
}

export class CaptureSession {
  private readonly t0 = performance.now()
  private readonly timeline: TimelineEvent[] = []
  private readonly cleanups: (() => void)[] = []
  private readonly startedAt = new Date().toISOString()

  private stream: MediaStream | null = null
  private ctx: AudioContext | null = null
  private node: AudioWorkletNode | null = null
  private recorder: MediaRecorder | null = null
  private readonly chunks: Float32Array[] = []
  private readonly recorded: Blob[] = []
  private frames = 0
  private startedWall: number | null = null
  private trackSettings: Record<string, unknown> | null = null
  private contextStateAtStart: string | null = null
  private error: string | null = null
  private maxTimer: ReturnType<typeof setTimeout> | null = null
  private startWatchdog: ReturnType<typeof setTimeout> | null = null
  private stopping: Promise<CaptureOutcome> | null = null
  private flushWaiter: (() => void) | null = null

  constructor(
    private readonly strategy: CaptureStrategy,
    private readonly voiceProcessing: boolean,
    private readonly onFinished: (outcome: CaptureOutcome) => void,
  ) {}

  /**
   * Must be called synchronously from a user gesture: the AudioContext is
   * created and resumed before the first await so iOS treats it as
   * user-initiated. Never throws; failures finish the session with an error.
   */
  start(): void {
    this.log('start-requested')
    this.listen(document, 'visibilitychange', () => {
      this.log(`visibility-${document.visibilityState}`)
    })
    this.listen(window, 'pagehide', () => {
      this.log('pagehide')
    })
    this.listen(window, 'pageshow', () => {
      this.log('pageshow')
    })

    if (this.strategy !== 'media-recorder') {
      try {
        this.ctx = this.createContext()
        this.contextStateAtStart = this.ctx.state
        const ctx = this.ctx
        this.listen(ctx, 'statechange', () => {
          this.log(`context-${ctx.state}`)
        })
        ctx.resume().catch((e: unknown) => {
          this.log(`context-resume-failed ${describeError(e)}`)
        })
      } catch (e) {
        this.fail(e)
        return
      }
    }

    this.begin().catch((e: unknown) => {
      this.fail(e)
    })
  }

  /** Idempotent: every caller receives the same outcome. */
  stop(reason: StopReason = 'user'): Promise<CaptureOutcome> {
    this.stopping ??= this.finish(reason).then((outcome) => {
      this.onFinished(outcome)
      return outcome
    })
    return this.stopping
  }

  private createContext(): AudioContext {
    return this.strategy === 'worklet-16k'
      ? new AudioContext({ sampleRate: 16000 })
      : new AudioContext()
  }

  private async begin(): Promise<void> {
    const v = this.voiceProcessing
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: v,
        noiseSuppression: v,
        autoGainControl: v,
      },
    })
    this.log('microphone-granted')
    if (this.isStopping()) {
      // Stopped while the permission prompt was open: release() has already
      // run, so turn the microphone off here.
      this.stream.getTracks().forEach((t) => {
        t.stop()
      })
      return
    }

    this.startWatchdog = setTimeout(() => {
      this.fail(
        new Error(
          `capture did not start within ${String(START_TIMEOUT_MS / 1000)} s of microphone grant (last step: ${this.lastStep()})`,
        ),
      )
    }, START_TIMEOUT_MS)

    const track = this.stream.getAudioTracks()[0]
    if (track === undefined) throw new Error('No audio track in stream')
    this.trackSettings = safeSettings(track.getSettings())
    this.listen(track, 'ended', () => {
      this.log('track-ended')
      void this.stop('track-ended')
    })
    this.listen(track, 'mute', () => {
      this.log('track-mute')
    })
    this.listen(track, 'unmute', () => {
      this.log('track-unmute')
    })

    if (this.strategy === 'media-recorder') {
      this.startRecorder(this.stream)
    } else {
      await this.startWorklet(this.stream)
    }

    if (this.isStopping()) return
    clearTimeout(this.startWatchdog)
    this.startedWall = performance.now()
    this.log('capturing')
    this.maxTimer = setTimeout(() => {
      void this.stop('max-duration')
    }, MAX_CAPTURE_SECONDS * 1000)
  }

  private async startWorklet(stream: MediaStream): Promise<void> {
    const ctx = this.ctx
    if (ctx === null) throw new Error('AudioContext missing')
    if (ctx.state !== 'running') await ctx.resume()
    this.log(`context-rate-${String(ctx.sampleRate)}`)

    const url = URL.createObjectURL(
      new Blob([CAPTURE_PROCESSOR_SOURCE], { type: 'application/javascript' }),
    )
    this.log('worklet-loading')
    try {
      await ctx.audioWorklet.addModule(url)
    } finally {
      URL.revokeObjectURL(url)
    }
    this.log('worklet-loaded')

    const source = ctx.createMediaStreamSource(stream)
    const node = new AudioWorkletNode(ctx, CAPTURE_PROCESSOR_NAME, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    })
    const maxFrames = MAX_CAPTURE_SECONDS * ctx.sampleRate
    node.port.onmessage = (event: MessageEvent<ProcessorMessage>) => {
      const msg = event.data
      if (msg.type === 'chunk') {
        this.chunks.push(msg.samples)
        this.frames += msg.samples.length
        if (this.frames >= maxFrames) void this.stop('max-duration')
      } else {
        this.flushWaiter?.()
      }
    }
    source.connect(node)
    node.connect(ctx.destination)
    this.node = node
  }

  private startRecorder(stream: MediaStream): void {
    const recorder = new MediaRecorder(stream)
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.recorded.push(e.data)
    }
    recorder.onerror = () => {
      this.log('recorder-error')
    }
    recorder.onpause = () => {
      this.log('recorder-pause')
    }
    recorder.onresume = () => {
      this.log('recorder-resume')
    }
    recorder.start(1000)
    this.recorder = recorder
    this.log(`recorder-mime-${recorder.mimeType || '(empty)'}`)
  }

  private fail(error: unknown): void {
    this.error = describeError(error)
    this.log(`error ${this.error}`)
    void this.stop('error')
  }

  private async finish(reason: StopReason): Promise<CaptureOutcome> {
    const lastStep = this.lastStep()
    this.log(`stop-${reason}`)
    const stoppedWall = performance.now()
    if (this.maxTimer !== null) clearTimeout(this.maxTimer)
    if (this.startWatchdog !== null) clearTimeout(this.startWatchdog)
    if (this.startedWall === null && this.error === null) {
      // Never report a silent empty capture: say where setup stopped.
      this.error = `stopped before capture began (last step: ${lastStep})`
    }

    let audio: Blob | null = null
    let output: CaptureReport['output'] = null
    let signal: SignalStats | null = null
    let capturedFrames: number | null = null
    let expectedFrames: number | null = null
    let decodeNote: string | null = null
    const wallClockSeconds =
      this.startedWall === null ? null : (stoppedWall - this.startedWall) / 1000

    try {
      if (this.recorder !== null) {
        audio = await this.stopRecorder(this.recorder)
        output = { mimeType: audio.type, sampleRate: null, bytes: audio.size }
        const decoded = await decode(audio)
        if (typeof decoded === 'string') {
          decodeNote = decoded
        } else {
          signal = analyseSignal(decoded.getChannelData(0), decoded.sampleRate)
          decodeNote = `decoded at ${String(decoded.sampleRate)} Hz, ${String(decoded.numberOfChannels)} ch (decoder may resample)`
        }
      } else if (this.node !== null && this.ctx !== null) {
        await this.flushWorklet(this.node)
        const rate = this.ctx.sampleRate
        let samples = concatChunks(this.chunks)
        const maxFrames = MAX_CAPTURE_SECONDS * rate
        if (samples.length > maxFrames) samples = samples.subarray(0, maxFrames)
        const wav = encodeWavPcm16(samples, rate)
        audio = new Blob([wav], { type: 'audio/wav' })
        output = { mimeType: 'audio/wav', sampleRate: rate, bytes: wav.byteLength }
        signal = analyseSignal(samples, rate)
        capturedFrames = samples.length
        if (wallClockSeconds !== null) {
          expectedFrames = Math.round(
            Math.min(wallClockSeconds, MAX_CAPTURE_SECONDS) * rate,
          )
        }
      }
    } catch (e) {
      this.error ??= describeError(e)
      this.log(`finalize-error ${describeError(e)}`)
    }

    const contextSampleRate = this.ctx?.sampleRate ?? null
    await this.release()

    return {
      audio,
      report: {
        strategy: this.strategy,
        voiceProcessing: this.voiceProcessing,
        startedAt: this.startedAt,
        stopReason: reason,
        error: this.error,
        wallClockSeconds,
        trackSettings: this.trackSettings,
        requestedSampleRate: this.strategy === 'worklet-16k' ? 16000 : null,
        contextSampleRate,
        contextStateAtStart: this.contextStateAtStart,
        output,
        capturedFrames,
        expectedFrames,
        captureRatio:
          capturedFrames !== null && expectedFrames !== null && expectedFrames > 0
            ? capturedFrames / expectedFrames
            : null,
        signal,
        decodeNote,
        timeline: this.timeline,
      },
    }
  }

  private flushWorklet(node: AudioWorkletNode): Promise<void> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.log('flush-timeout')
        resolve()
      }, FLUSH_TIMEOUT_MS)
      this.flushWaiter = () => {
        clearTimeout(timer)
        resolve()
      }
      node.port.postMessage({ type: 'flush' })
    })
  }

  private stopRecorder(recorder: MediaRecorder): Promise<Blob> {
    return new Promise((resolve) => {
      const done = () => {
        resolve(new Blob(this.recorded, { type: recorder.mimeType }))
      }
      if (recorder.state === 'inactive') {
        done()
        return
      }
      recorder.onstop = done
      recorder.stop()
    })
  }

  private async release(): Promise<void> {
    for (const cleanup of this.cleanups) cleanup()
    this.node?.disconnect()
    this.stream?.getTracks().forEach((t) => {
      t.stop()
    })
    if (this.ctx !== null && this.ctx.state !== 'closed') {
      await this.ctx.close().catch(() => undefined)
    }
  }

  private listen(target: EventTarget, type: string, handler: () => void): void {
    target.addEventListener(type, handler)
    this.cleanups.push(() => {
      target.removeEventListener(type, handler)
    })
  }

  /** A method, not a field read, so checks after an await are not narrowed away. */
  private isStopping(): boolean {
    return this.stopping !== null
  }

  private lastStep(): string {
    return this.timeline.at(-1)?.event ?? 'none'
  }

  private log(event: string): void {
    this.timeline.push({ t: Math.round(performance.now() - this.t0), event })
  }
}

/** Drops device identifiers; keeps the audio parameters the browser granted. */
function safeSettings(settings: MediaTrackSettings): Record<string, unknown> {
  const out: Record<string, unknown> = { ...settings }
  delete out.deviceId
  delete out.groupId
  return out
}

async function decode(blob: Blob): Promise<AudioBuffer | string> {
  const ctx = new AudioContext()
  try {
    return await ctx.decodeAudioData(await blob.arrayBuffer())
  } catch (e) {
    return `decode failed: ${describeError(e)}`
  } finally {
    await ctx.close().catch(() => undefined)
  }
}
